// constants
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { PDP_EVENTS, SIMPLE } from 'shared/helpers/analytics/constants/pdpEvents';

// types
import { IColor } from 'shared/store/details/types';
import { DeliveryDataInterface, DeliveryOrShippingDateInterface, StringifyData } from './types';

// helpers
import storage from 'shared/helpers/storage';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import { pushRetinaDataInDatalayer } from 'shared/helpers/analytics/helpers';

const DELIVERY = 'Delivery';
const SHIPPING = 'Shipping';
const DELIVERY_EXPERIMENT_KEY = 'deliveryExperiment';

interface UrgencyData {
  text: string;
  enabled: boolean;
  textColor: string;
  identifier: number;
}
interface IPageLoadProps {
  title: string;
  subTitle: string;
  availableSizes: string;
  parentSKU: string;
  productID: string;
  isOneSize: boolean;
  salePrice: string;
  price: string;
  tags: string;
  reviewStarRating: number;
  reviewStarRatingCount: number;
  reviewCount: number;
  isSizeGuideAvailable: string;
  colorVariants: string;
  isVideoAvailable: boolean;
  disclaimerTextShown?: boolean;
  areResponsibleTagsAvailable: boolean;
  outOfStockSize: string;
  colorConfig: string;
  type: string;
  isOutOfStock: number;
  imageUrl: string;
  isReturnable: boolean;
  categoryName: string;
  isPriceReveal: boolean;
  isMobile: boolean;
  currency: string;
  discountPercentage: number;
  categoryLevelString: string;
  selectedSizeName: string;
  selectedSKUID: string;
  productBadge: string;
  pincodeOnPdp: string;
  urgencyNudgeData: UrgencyData;
  isFomoTimerVisible: boolean;
  isSalePriceLive: boolean;
  isViewSimilarAvailable: boolean;
  isDetailsSectionAvailable: boolean;
  isAplusContentAvailable: boolean;
  isModelDetailsAvailable: boolean;
  isNewSizeChartEnabled: boolean;
  isSizeChartDataAvailable: boolean;
  isGamificationAggresive: number;
  isGamificationEnabled: boolean;
  inventoryNudge?: string;
  isCategoryLaunchingSoon?: boolean;
  isBrandBookData: {
    productID: string;
    widget_type: string;
    items: [];
    title?: string;
  }[];
  isSpecialTagsPresent: boolean;
  specialTagName: string;
  returnNudgeApplicable: boolean;
  catalog_tag?: string[];
}

export const getColorVariants = (colorOptions: IColor[], defaultColor: IColor) => {
  const colorVariants = colorOptions.map((item: IColor) => item.name).join(', ');

  if (colorOptions.length > 0) {
    return `${colorOptions.length + 1}:${defaultColor}, ${colorVariants}`;
  }

  return '';
};

export const stringifyTags = (tags: string[]) => tags.join(',');

// eslint-disable-next-line no-undef
export const getSizes = (sizeOptions: ProductDetail.ISizeOption[]) => {
  const outOfStockSizes: string[] = [],
    inStockSizes: string[] = [];

  sizeOptions.forEach((item: any) => {
    if (!item.isOutOfStock) {
      inStockSizes.push(item.sizeName);
    } else outOfStockSizes.push(item.sizeName);
  });
  return {
    inStockSizes,
    outOfStockSizes,
  };
};

export const getCategoryLevelString = (categories: {
  [key: string]: { id: string; name: string; };
}) => {
  const categoryNames: string[] = [];
  const categoryKeys = Object.keys(categories).sort();
  categoryKeys.forEach((category) => {
    categoryNames.push(categories[category]?.name);
  });

  return categoryNames;
};

export const getDynamicCategoryLevels = (categoryLevelString: string) => {
  const names = categoryLevelString.split(',');
  const levels: { [key: string]: string; } = {};

  for (let i = 1; i < names.length; i++) {
    levels[`categoryLevel${i}`] = names[i];
  }
  return levels;
};

const getVariantType = (selectedSizeName: string, type: string) => {
  if (type == SIMPLE || !selectedSizeName) {
    return undefined;
  }

  return selectedSizeName;
};

const getVariantProductID = (selectedSKUID: string, type: string) => {
  if (type == SIMPLE || !selectedSKUID) {
    return undefined;
  }

  return selectedSKUID;
};

export const onPageLoad = ({
  title,
  subTitle,
  availableSizes,
  parentSKU,
  productID,
  salePrice,
  price,
  tags,
  reviewStarRating,
  reviewStarRatingCount,
  reviewCount,
  isSizeGuideAvailable,
  colorVariants,
  isVideoAvailable,
  disclaimerTextShown = false,
  outOfStockSize,
  colorConfig,
  type,
  isOutOfStock,
  imageUrl,
  categoryName,
  isPriceReveal,
  currency,
  discountPercentage,
  categoryLevelString,
  selectedSizeName,
  selectedSKUID,
  productBadge,
  pincodeOnPdp,
  urgencyNudgeData,
  isFomoTimerVisible,
  isViewSimilarAvailable,
  isDetailsSectionAvailable,
  isAplusContentAvailable,
  isModelDetailsAvailable,
  isSalePriceLive,
  isNewSizeChartEnabled,
  isSizeChartDataAvailable,
  isBrandBookData,
  isGamificationEnabled,
  isGamificationAggresive,
  inventoryNudge,
  isSpecialTagsPresent,
  specialTagName,
  isCategoryLaunchingSoon,
  returnNudgeApplicable,
  catalog_tag,
}: IPageLoadProps) => {
  const isOutOfStockProduct =
    isOutOfStock ||
    (outOfStockSize.length > 0 &&
      selectedSizeName.length > 0 &&
      outOfStockSize.includes(selectedSizeName));

  //Pdp Sections data mapping
  const pdpSectionsAvailable = {
    viewSimilarBtn: isViewSimilarAvailable,
    colorVariant: colorConfig,
    ratings: reviewStarRatingCount,
    sizeGuide: isSizeGuideAvailable,
    readAllReview: reviewCount > 1,
    sizeSelector: availableSizes.length,
    productInfo: isDetailsSectionAvailable,
    carousalVideo: isVideoAvailable,
    aplusContent: isAplusContentAvailable,
    disclaimer: disclaimerTextShown,
    productTag: tags,
    modelDetail: isModelDetailsAvailable,
  };
  const pdpSectionsList: string[] = [];
  const pdpSectionNames = Object.keys(pdpSectionsAvailable);
  Object.values(pdpSectionsAvailable).map((sectionData, i) => {
    if (sectionData) return pdpSectionsList.push(pdpSectionNames[i]);
  });

  const showUrgencyNudge =
    urgencyNudgeData?.enabled && urgencyNudgeData?.text?.length > 0 && !isOutOfStock;

  const brandBookTrackingData = {
    brandBookVisible: isBrandBookData?.length ? 'shown' : '',
    brandBookHighlightsAttribute: isBrandBookData[0]?.items,
    brandBookHighlightsLength: isBrandBookData[0]?.items.length,
  };
  pushRetinaDataInDatalayer({ pdpProductId: productID, pdpProductName: subTitle });

  NFA.track({
    type: TRACK_TYPE.PAGELOAD,
    event: PDP_EVENTS.PAGELOAD,
    data: {
      productID,
      productType: type,
      productName: subTitle,
      productBrandName: title,
      productTags: tags,
      currency,
      isOutOfStock: Boolean(isOutOfStock),
      MRP: price,
      discountedPrice: salePrice,
      discountPercent: discountPercentage,
      isVideoAvailable,
      disclaimerTextShown,
      ...getDynamicCategoryLevels(categoryLevelString),
      availableSizes,
      outOfStockSizes: outOfStockSize,
      variantType: getVariantType(selectedSizeName, type),
      variantProductID: getVariantProductID(selectedSKUID, type),
      productBadge,
      isOffersAvailable: undefined,
      isCouponsAvailable: undefined,
      pincodeOnPdp,
      isUrgencyTagShown: showUrgencyNudge ? urgencyNudgeData?.enabled : undefined,
      urgencyTagType: showUrgencyNudge ? urgencyNudgeData?.text : undefined,
      urgencyTagIdentifier: showUrgencyNudge ? urgencyNudgeData?.identifier : undefined,
      isPriceRevealProduct: isPriceReveal,
      isFomoTimerVisible: isOutOfStockProduct ? false : isFomoTimerVisible,
      productColorName: colorConfig,
      isSalePriceLive,
      parentSKU,
      categoryName,
      firstImageUrl: imageUrl,
      pdpReviewCount: reviewCount,
      pdpOverallRating: reviewStarRating,
      verifiedRatingsCount: reviewStarRatingCount,
      colorVariants: colorVariants.split(':')[1],
      colorVariantsCount: colorVariants.split(':')[0],
      isRatingSummaryPresent: Boolean(reviewStarRatingCount || reviewCount),
      pdpSectionsData: pdpSectionsList,
      isNewSizeChartEnabled,
      isSizeChartDataAvailable,
      brandBookTrackingData,
      isGamificationEnabled,
      isGamificationAggresive,
      inventoryNudge,
      isSpecialTagsPresentInTopN: isSpecialTagsPresent,
      specialTagName: specialTagName,
      is_category_launching_soon: isCategoryLaunchingSoon || false,
      return_nudge_applicable: returnNudgeApplicable,
      ...(catalog_tag ? { catalog_tags: catalog_tag } : {}),
    },
  });
};

// adding logic to calclulate shipping and delivery dates
export const deliveryData = (delivery: DeliveryDataInterface): DeliveryOrShippingDateInterface => {
  try {
    let minDelivery = Number.MAX_SAFE_INTEGER;
    let minShipping = Number.MAX_SAFE_INTEGER;

    // if (!delivery.deliveryDataForSKUs) return { minShipping: -1 };

    Object.keys(delivery.deliveryDataForSKUs).forEach((key) => {
      const sku = delivery.deliveryDataForSKUs[key];
      if (sku.o2sTat < minShipping) {
        minShipping = sku.o2sTat;
      }
      if (sku.o2dTat) {
        if (sku.o2dTat < minDelivery) {
          minDelivery = sku.o2dTat;
        }
      }
    });
    if (minDelivery === Number.MAX_SAFE_INTEGER) minDelivery = null;
    return { minDelivery: minDelivery, minShipping: minShipping };
  } catch {
    // do nothing
  }
};

export const setTrackingToStorage = (dataObj: DeliveryOrShippingDateInterface) => {
  try {
    const deliveryOrShippingDays = dataObj.minDelivery ? dataObj.minDelivery : dataObj.minShipping;

    const deliveryOrShippingText = dataObj.minDelivery ? DELIVERY : SHIPPING;
    const objectToSend = {
      type: deliveryOrShippingText,
      days: deliveryOrShippingDays,
    };
    storage.setItem(DELIVERY_EXPERIMENT_KEY, objectToSend);
  } catch {
    // do nothing
  }
};

export const stringifyData = ({
  tags,
  options,
  colorOptions,
  defaultColor,
  categoryLevels,
}: StringifyData) => {
  const tagsStringified = stringifyTags(tags);
  const { inStockSizes, outOfStockSizes } = getSizes(options);
  const colors = getColorVariants(colorOptions, defaultColor);
  const categoryLevelNames = getCategoryLevelString(categoryLevels);

  return {
    tagsStr: tagsStringified,
    availableSizes: inStockSizes.join(','),
    colorVariants: colors,
    outOfStockSize: outOfStockSizes.join(','),
    categoryLevelString: categoryLevelNames.join(','),
  };
};
